import { useState } from 'react';

interface GoogleSearchPreviewProps {
  title?: string | null;
  fallbackTitle?: string | null;
  description?: string | null;
  fallbackDescription?: string | null;
  urlPath?: string | null;
  noIndex?: boolean;
}

export function GoogleSearchPreview({
  title,
  fallbackTitle,
  description,
  fallbackDescription,
  urlPath = '',
  noIndex = false,
}: GoogleSearchPreviewProps) {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');

  const rawTitle = (title?.trim() || fallbackTitle?.trim() || 'Sayfa Başlığı');
  const fullTitle = `${rawTitle} | Hurmahane`;
  const titleCharCount = rawTitle.length;

  const cleanFallback = fallbackDescription
    ? fallbackDescription.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    : '';

  const rawDescription =
    description?.trim() ||
    cleanFallback ||
    'Bu sayfa için henüz bir açıklama girilmedi. Arama motorları sayfa içeriğinden otomatik bir özet oluşturacaktır.';
  const descCharCount = (description?.trim() || '').length;

  // URL breadcrumb
  const cleanPath = urlPath?.replace(/^\/+/, '') || '';
  const breadcrumbParts = cleanPath ? cleanPath.split('/') : ['sayfa'];
  const breadcrumbDisplay = ['hurmahane.com', ...breadcrumbParts].join(' › ');

  // Limits
  const TITLE_RECOMMENDED_MAX = 60;
  const DESC_RECOMMENDED_MAX = 160;

  return (
    <div className="rounded-xl border border-ink-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 pb-3">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-50 text-blue-600 font-bold text-xs">
            G
          </span>
          <span className="text-xs font-semibold uppercase tracking-wider text-ink-600">
            Google Arama Önizlemesi
          </span>
        </div>

        {/* Device toggle */}
        <div className="flex items-center rounded-lg border border-ink-200 bg-ink-50 p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setDevice('desktop')}
            className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 font-medium transition ${
              device === 'desktop'
                ? 'bg-white text-ink-900 shadow-sm'
                : 'text-ink-500 hover:text-ink-800'
            }`}
          >
            <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.75">
              <rect x="2" y="3" width="16" height="11" rx="2" />
              <path d="M7 17h6M10 14v3" />
            </svg>
            Masaüstü
          </button>
          <button
            type="button"
            onClick={() => setDevice('mobile')}
            className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 font-medium transition ${
              device === 'mobile'
                ? 'bg-white text-ink-900 shadow-sm'
                : 'text-ink-500 hover:text-ink-800'
            }`}
          >
            <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.75">
              <rect x="5" y="2" width="10" height="16" rx="2" />
              <path d="M9 15h2" strokeLinecap="round" />
            </svg>
            Mobil
          </button>
        </div>
      </div>

      {noIndex ? (
        <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 border border-amber-200">
          <span className="text-amber-600 text-sm">⚠️</span>
          <span>
            <strong>noindex aktif:</strong> Bu sayfa arama motorları tarafından dizine eklenmeyecektir (SERP sonuçlarında çıkmaz).
          </span>
        </div>
      ) : null}

      {/* Snippet Card */}
      <div
        className={`rounded-lg border border-ink-100 bg-white p-4 font-sans ${
          device === 'mobile' ? 'max-w-sm shadow-inner' : 'max-w-xl'
        }`}
      >
        {/* Favicon & Breadcrumb */}
        <div className="mb-1 flex items-center gap-2">
          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-ink-100 text-[10px] font-bold text-ink-700">
            E
          </div>
          <div className="min-w-0 flex-1 leading-none">
            <p className="truncate text-xs font-normal text-[#202124]">E-Ticaret</p>
            <p className="truncate text-[11px] text-[#4d5156]">{breadcrumbDisplay}</p>
          </div>
          <button type="button" className="text-ink-400 hover:text-ink-600" title="Seçenekler">
            <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor">
              <circle cx="10" cy="4" r="1.5" />
              <circle cx="10" cy="10" r="1.5" />
              <circle cx="10" cy="16" r="1.5" />
            </svg>
          </button>
        </div>

        {/* Clickable Title */}
        <h3
          className={`cursor-pointer font-normal text-[#1a0dab] hover:underline ${
            device === 'mobile' ? 'text-base leading-snug line-clamp-2' : 'text-lg leading-snug truncate'
          }`}
          title={fullTitle}
        >
          {fullTitle}
        </h3>

        {/* Snippet Description */}
        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-[#4d5156]">
          {rawDescription}
        </p>
      </div>

      {/* SEO Health & Character Counters */}
      <div className="mt-3 grid gap-2 pt-3 border-t border-ink-100 sm:grid-cols-2 text-xs">
        <div className="flex items-center justify-between rounded-lg bg-ink-50 px-3 py-1.5">
          <span className="text-ink-600 font-medium">Başlık Uzunluğu:</span>
          <span
            className={`font-mono font-semibold ${
              titleCharCount === 0
                ? 'text-ink-400'
                : titleCharCount > TITLE_RECOMMENDED_MAX
                ? 'text-amber-600'
                : 'text-emerald-600'
            }`}
          >
            {titleCharCount} / {TITLE_RECOMMENDED_MAX} kr.
            {titleCharCount > TITLE_RECOMMENDED_MAX ? ' (Uzun)' : titleCharCount >= 30 ? ' (İdeal)' : ''}
          </span>
        </div>

        <div className="flex items-center justify-between rounded-lg bg-ink-50 px-3 py-1.5">
          <span className="text-ink-600 font-medium">Açıklama Uzunluğu:</span>
          <span
            className={`font-mono font-semibold ${
              descCharCount === 0
                ? 'text-ink-400'
                : descCharCount > DESC_RECOMMENDED_MAX
                ? 'text-amber-600'
                : 'text-emerald-600'
            }`}
          >
            {descCharCount} / {DESC_RECOMMENDED_MAX} kr.
            {descCharCount > DESC_RECOMMENDED_MAX ? ' (Uzun)' : descCharCount >= 80 ? ' (İdeal)' : ''}
          </span>
        </div>
      </div>
    </div>
  );
}
